import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { SpeakerDetailsInterface } from "@/types/SpeakerDetailsInterface";

interface SpeakerHeaderProps {
  speaker: SpeakerDetailsInterface;
  onOpenLightbox: () => void;
}

const BIO_CHAR_LIMIT = 450;

export const SpeakerHeader = ({ speaker, onOpenLightbox }: SpeakerHeaderProps) => {
  const [bioExpanded, setBioExpanded] = useState(false);
  const bioIsLong = speaker.bio && speaker.bio.length > BIO_CHAR_LIMIT;
  const displayBio = bioIsLong && !bioExpanded
    ? speaker.bio.slice(0, BIO_CHAR_LIMIT).trimEnd() + '…'
    : speaker.bio;

  return (
    <div className="flex flex-col lg:flex-row gap-8 items-start mb-8">
      {/* Large Profile Photo - responsive width */}
      <div className="w-full md:w-1/2 lg:w-1/3 max-h-96 flex-shrink-0 -mt-8 mb-0 rounded-b-lg overflow-hidden">
        <div className="cursor-pointer" onClick={onOpenLightbox}>
            <img
                src={speaker.imageUrl}
                alt={speaker.name}
                className="w-full h-auto object-cover rounded-b-lg shadow-lg hover:opacity-90 transition-opacity"/>
        </div>
      </div>

      {/* Content floating to the right */}
      <div className="flex-1 space-y-6 lg:pr-8">
        <div>
          <h1 className="text-4xl font-bold mb-2">{speaker.name}</h1>
          <p className="text-xl text-muted-foreground mb-1">{speaker.title}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {Array.isArray(speaker.topics) && speaker.topics.map((topicObj) => (
              <Badge key={topicObj.id} variant="secondary" className="text-sm hover:bg-primary hover:text-primary-foreground transition-colors">
                  {topicObj.name}
              </Badge>
          ))}
        </div>

        <div className="mt-4 mb-6">
          <p className="text-lg leading-relaxed text-muted-foreground">
            {displayBio}
          </p>
          {bioIsLong && (
            <Button
              variant="link"
              className="px-0 h-auto text-base text-primary mt-1"
              onClick={() => setBioExpanded(!bioExpanded)}
            >
              {bioExpanded ? (
                <>Show less <ChevronUp className="w-4 h-4 ml-1" /></>
              ) : (
                <>Show more <ChevronDown className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};